import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  addCrmContactChannel,
  contactFieldKeys,
  getCrmContact,
  unlinkCrmContactChannel,
  updateCrmContact,
  updateCrmContactChannel,
  type CrmContactChannel,
} from '@/entities/crm-contacts';
import { shownPhone } from '@/entities/chats';
import { ApiError } from '@/shared/api';
import { crmName, labelOf, useI18n } from '@/shared/i18n';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { Input } from '@/shared/ui/shadcn/input';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { HeaderAction, PageHeader } from '@/widgets/page-header';

const CHANNEL_KINDS = ['phone', 'email', 'whatsapp', 'telegram'] as const;
const CHANNEL_KIND_SET = new Set<string>(CHANNEL_KINDS);

function isUsableChannel(channel: CrmContactChannel) {
  if (!CHANNEL_KIND_SET.has(channel.kind)) return false;
  const value = channel.value?.trim() ?? '';
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    return false;
  }
  if (channel.kind === 'phone' || channel.kind === 'whatsapp') {
    return Boolean(shownPhone(value) || value);
  }
  return value.length > 0;
}

export function CrmContactPage() {
  const { t } = useI18n();
  const { contactId } = useParams();
  const [search] = useSearchParams();
  const queryClient = useQueryClient();

  const [nameDraft, setNameDraft] = useState('');
  const [fieldDrafts, setFieldDrafts] = useState<Record<string, string>>({});
  const [channelOpen, setChannelOpen] = useState(false);
  const [channelKind, setChannelKind] = useState<string>('phone');
  const [channelValue, setChannelValue] = useState('');
  const [channelDrafts, setChannelDrafts] = useState<Record<string, string>>({});

  const contactQuery = useQuery({
    queryKey: ['crm-contact', contactId],
    queryFn: () => getCrmContact(contactId ?? ''),
    enabled: Boolean(contactId),
  });

  const contact = contactQuery.data;
  const fromCard = search.get('card')?.trim() ?? '';
  const backCardId = useMemo(() => {
    if (fromCard) return fromCard;
    return contact?.cards?.[0]?.id ?? '';
  }, [fromCard, contact?.cards]);
  const backPipeline = useMemo(() => {
    if (!backCardId || !contact?.cards) return '';
    return contact.cards.find((card) => card.id === backCardId)?.pipeline ?? '';
  }, [backCardId, contact?.cards]);
  const backTo = backCardId
    ? `/crm/cards/${encodeURIComponent(backCardId)}${
        backPipeline ? `?pipeline=${encodeURIComponent(backPipeline)}` : ''
      }`
    : '/contacts';
  const backLabel = backCardId ? t.crm.backToCard : t.crm.backToContacts;

  const fieldKeys = useMemo(() => contactFieldKeys(contact?.fields), [contact?.fields]);
  const channels = useMemo(
    () => (contact?.channels ?? []).filter(isUsableChannel),
    [contact?.channels],
  );

  useEffect(() => {
    if (!contact) return;
    setNameDraft(contact.name ?? '');
    const drafts: Record<string, string> = {};
    for (const key of contactFieldKeys(contact.fields)) {
      drafts[key] = contact.fields?.[key] ?? '';
    }
    setFieldDrafts(drafts);
    const nextChannels: Record<string, string> = {};
    for (const channel of (contact.channels ?? []).filter(isUsableChannel)) {
      if (channel.id) nextChannels[channel.id] = channel.value;
    }
    setChannelDrafts(nextChannels);
  }, [contact?.id, contact?.name, contact?.fields, contact?.channels]);

  const save = useMutation({
    mutationFn: (input: { name?: string; fields?: Record<string, string> }) =>
      updateCrmContact(contactId ?? '', input),
    onSuccess: async () => {
      toast.success(t.crm.saved);
      await queryClient.invalidateQueries({ queryKey: ['crm-contact', contactId] });
      await queryClient.invalidateQueries({ queryKey: ['crm-contacts'] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.saveFailed);
    },
  });

  const addChannel = useMutation({
    mutationFn: (input: { kind: string; value: string }) =>
      addCrmContactChannel(contactId ?? '', input),
    onSuccess: async () => {
      setChannelOpen(false);
      setChannelValue('');
      toast.success(t.crm.saved);
      await queryClient.invalidateQueries({ queryKey: ['crm-contact', contactId] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.channelAddFailed);
    },
  });

  const saveChannel = useMutation({
    mutationFn: (input: { channelId: string; value: string }) =>
      updateCrmContactChannel(contactId ?? '', input.channelId, { value: input.value }),
    onSuccess: async () => {
      toast.success(t.crm.saved);
      await queryClient.invalidateQueries({ queryKey: ['crm-contact', contactId] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.channelSaveFailed);
    },
  });

  const unlinkChannel = useMutation({
    mutationFn: (channelId: string) => unlinkCrmContactChannel(contactId ?? '', channelId),
    onSuccess: async () => {
      toast.success(t.crm.saved);
      await queryClient.invalidateQueries({ queryKey: ['crm-contact', contactId] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t.crm.channelUnlinkFailed);
    },
  });

  const saving =
    save.isPending || addChannel.isPending || saveChannel.isPending || unlinkChannel.isPending;
  const missing =
    contactQuery.error instanceof ApiError && contactQuery.error.status === 404;

  const commitName = () => {
    if (!contact) return;
    const next = nameDraft.trim();
    if (!next || next === (contact.name ?? '').trim()) return;
    save.mutate({ name: next });
  };

  const commitField = (key: string) => {
    if (!contact) return;
    const next = (fieldDrafts[key] ?? '').trim();
    const current = contact.fields?.[key]?.trim() ?? '';
    if (next === current) return;
    save.mutate({ fields: { [key]: next } });
  };

  const commitChannel = (channel: CrmContactChannel) => {
    if (!channel.id) return;
    const next = (channelDrafts[channel.id] ?? '').trim();
    if (!next || next === channel.value.trim()) return;
    saveChannel.mutate({ channelId: channel.id, value: next });
  };

  return (
    <div className="absolute inset-0 flex min-h-0 flex-col overflow-hidden">
      <PageHeader
        title={contact?.name || t.crm.contactTitle}
        description={t.crm.contactDescription}
        leadingMode="always"
        leading={
          <HeaderAction label={backLabel} render={<Link to={backTo} />}>
            <ArrowLeft className="size-4" />
          </HeaderAction>
        }
      />
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-4">
        {contactQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">{t.common.loading}</p>
        ) : missing ? (
          <p className="text-sm text-destructive">{t.crm.contactNotFound}</p>
        ) : contactQuery.isError ? (
          <p className="text-sm text-destructive">{t.crm.contactLoadFailed}</p>
        ) : contact ? (
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
            <dl className="grid gap-4 desktop:grid-cols-2">
              <div className="space-y-0.5 desktop:col-span-2">
                <dt className="text-[11px] tracking-wide text-muted-foreground uppercase">
                  {t.common.name}
                </dt>
                <dd>
                  <Input
                    value={nameDraft}
                    onChange={(event) => setNameDraft(event.target.value)}
                    onBlur={commitName}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur();
                    }}
                    disabled={saving}
                    aria-label={t.common.name}
                  />
                </dd>
              </div>
              {fieldKeys.map((key) => (
                <div key={key} className="space-y-0.5">
                  <dt className="text-[11px] tracking-wide text-muted-foreground uppercase">
                    {labelOf(t.fields, key)}
                  </dt>
                  <dd>
                    <Input
                      value={fieldDrafts[key] ?? ''}
                      onChange={(event) =>
                        setFieldDrafts((prev) => ({ ...prev, [key]: event.target.value }))
                      }
                      onBlur={() => commitField(key)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') event.currentTarget.blur();
                      }}
                      disabled={saving}
                      aria-label={labelOf(t.fields, key)}
                    />
                  </dd>
                </div>
              ))}
            </dl>

            <section className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-[11px] tracking-wide text-muted-foreground uppercase">
                  {t.crm.handles}
                </h2>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        disabled={saving}
                        aria-label={t.crm.channelAdd}
                      />
                    }
                  >
                    <Plus className="size-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {CHANNEL_KINDS.map((kind) => (
                      <DropdownMenuItem
                        key={kind}
                        onClick={() => {
                          setChannelKind(kind);
                          setChannelValue('');
                          setChannelOpen(true);
                        }}
                      >
                        {labelOf(t.handleKinds, kind)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {channels.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t.crm.handlesEmpty}</p>
              ) : (
                <ul className="space-y-1">
                  {channels.map((channel) => (
                    <li
                      key={channel.id || `${channel.kind}:${channel.value}`}
                      className="group flex items-center gap-2 rounded-md px-1 py-1.5 hover:bg-muted/60"
                    >
                      <span className="w-20 shrink-0 text-[11px] tracking-wide text-muted-foreground uppercase">
                        {labelOf(t.handleKinds, channel.kind)}
                      </span>
                      <Input
                        className="min-w-0 flex-1"
                        value={channelDrafts[channel.id] ?? channel.value}
                        onChange={(event) =>
                          setChannelDrafts((prev) => ({
                            ...prev,
                            [channel.id]: event.target.value,
                          }))
                        }
                        onBlur={() => commitChannel(channel)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') event.currentTarget.blur();
                        }}
                        disabled={saving || !channel.id}
                        aria-label={labelOf(t.handleKinds, channel.kind)}
                      />
                      {channel.dialog_id ? (
                        <Link
                          to={`/chats/${encodeURIComponent(channel.dialog_id)}`}
                          className="shrink-0 text-[11px] tracking-wide text-muted-foreground uppercase hover:underline"
                        >
                          {t.nav.chats}
                        </Link>
                      ) : null}
                      {channel.is_primary ? (
                        <span className="shrink-0 text-[11px] tracking-wide text-muted-foreground uppercase">
                          {t.crm.contactMain}
                        </span>
                      ) : null}
                      {channel.id ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="opacity-60 group-hover:opacity-100"
                          disabled={saving}
                          aria-label={t.crm.channelUnlink}
                          onClick={() => unlinkChannel.mutate(channel.id)}
                        >
                          <X className="size-3.5" />
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="space-y-3">
              <h2 className="text-[11px] tracking-wide text-muted-foreground uppercase">
                {t.crm.linkedCards}
              </h2>
              {(contact.cards?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">{t.crm.linkedCardsEmpty}</p>
              ) : (
                <ul className="space-y-2">
                  {contact.cards.map((card) => (
                    <li key={card.id}>
                      <Link
                        to={`/crm/cards/${encodeURIComponent(card.id)}${
                          card.pipeline
                            ? `?pipeline=${encodeURIComponent(card.pipeline)}`
                            : ''
                        }`}
                        className="block rounded-md px-3 py-2 text-sm hover:bg-muted"
                      >
                        <span className="font-medium">
                          {card.display_name || t.common.lead}
                        </span>
                        {card.is_main ? (
                          <span className="ml-2 text-[11px] font-normal tracking-wide text-muted-foreground uppercase">
                            {t.crm.contactMain}
                          </span>
                        ) : null}
                        <span className="mt-0.5 block text-[11px] text-muted-foreground">
                          {[crmName(t.crm.names, card.pipeline), crmName(t.crm.names, card.stage)]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
      </ScrollArea>

      <Dialog
        open={channelOpen}
        onOpenChange={(open) => {
          if (!open && !addChannel.isPending) {
            setChannelOpen(false);
            setChannelValue('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.crm.channelCreate}</DialogTitle>
            <DialogDescription>{t.crm.channelCreateHint}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={channelKind} onValueChange={(value) => value && setChannelKind(value)}>
              <SelectTrigger className="w-full" aria-label={t.crm.channelKind}>
                <SelectValue>
                  {(value) => labelOf(t.handleKinds, String(value ?? ''))}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {CHANNEL_KINDS.map((kind) => (
                  <SelectItem key={kind} value={kind}>
                    {labelOf(t.handleKinds, kind)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={channelValue}
              onChange={(event) => setChannelValue(event.target.value)}
              placeholder={t.crm.channelValuePlaceholder}
              disabled={addChannel.isPending}
              autoFocus
              aria-label={t.crm.channelValuePlaceholder}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={addChannel.isPending}
              onClick={() => {
                setChannelOpen(false);
                setChannelValue('');
              }}
            >
              {t.crm.cancel}
            </Button>
            <Button
              type="button"
              disabled={addChannel.isPending || channelValue.trim() === ''}
              onClick={() =>
                addChannel.mutate({ kind: channelKind, value: channelValue.trim() })
              }
            >
              {addChannel.isPending ? t.common.creating : t.crm.channelCreate}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
